/* eslint-disable @typescript-eslint/no-non-null-assertion */
import type { Metadata, ReadApiAssetList } from "@metaplex-foundation/js";
import { Metaplex, toNft } from "@metaplex-foundation/js";
import { useConnection } from "@solana/wallet-adapter-react";
import { PublicKey } from "@solana/web3.js";
import type { NFT } from "@stepfinance/nft";
import { useQuery } from "@tanstack/react-query";

import {
  getAssetsByOwner,
  toMetadataFromReadApiAsset,
  toMintFromReadApiAsset,
  toNftEditionFromReadApiAsset,
} from "../../../utils/nft/readApi";
import { convertNFTNameToCollectionName } from "../../../utils/nft/string-utils";
import { useConnectedWallet } from "../../wallet";

export interface CompressedNFTResponse {
  compressedNftMap: Map<string, NFT>;
  readApiAssets: ReadApiAssetList;
  collectionList: string[];
}

export const useCompressedNFTsQuery = () => {
  const wallet = useConnectedWallet();
  const { connection } = useConnection();

  return useQuery<CompressedNFTResponse>(
    ["compressedNFTs", wallet?.publicKey.toBase58()],
    async () => {
      const readApiAssets = await getAssetsByOwner(connection.rpcEndpoint, {
        ownerAddress: wallet!.publicKey.toBase58(),
      });

      const metaplex = new Metaplex(connection);

      const compressedNftMap: Map<string, NFT> = new Map();

      if (readApiAssets.items.length === 0) {
        return { compressedNftMap, readApiAssets, collectionList: [] };
      }

      const collectionKeys: { [key: string]: PublicKey } = {};
      readApiAssets.items.forEach((asset) => {
        if (!asset.compression.compressed || asset["burnt"] === true) {
          return;
        }
        const key = asset.grouping[0].group_value;
        if (!collectionKeys[key]) {
          collectionKeys[key] = new PublicKey(key);
        }
      });

      const collectionMetadatas = (
        await metaplex.nfts().findAllByMintList({ mints: Object.values(collectionKeys) })
      ).filter((x): x is Metadata => !!x);

      const collectionNftsMap = new Map(collectionMetadatas.map((nft) => [nft.mintAddress.toBase58(), nft]));
      const collectionList = collectionMetadatas.map((nft) => nft.name);

      for (const asset of readApiAssets.items) {
        if (!asset.compression.compressed || asset["burnt"] === true) {
          continue;
        }

        const mint = toMintFromReadApiAsset(asset);
        const edition = toNftEditionFromReadApiAsset(asset);
        const metadata = toMetadataFromReadApiAsset(asset);
        const metaplexNft = toNft(metadata, mint, edition);

        const collectionMetadata = collectionNftsMap.get(asset.grouping[0].group_value);

        const nft = {
          ...metaplexNft,
          ownership: asset.ownership,
          owner: wallet!.publicKey.toBase58(),
          amount: 1,
          floorPrice: 0,
          floorPriceUSD: 0,
          collectionName: collectionMetadata?.name ?? convertNFTNameToCollectionName(metadata.name) ?? "",
          sfLoaded: false,
          solanaFloorData: null,
        };

        compressedNftMap.set(nft.address.toBase58(), nft);
      }

      return { compressedNftMap, readApiAssets, collectionList };
    },
    {
      enabled: !!wallet,
      staleTime: 30000,
    },
  );
};
